import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Leaf, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode, userType = "candidate" }) {
  const isRegister = mode === "register";
  const isEmployer = userType === "employer";
  const { login, register, registerEmployer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let u;
      if (isRegister) {
        u = isEmployer
          ? await registerEmployer(form.company_name, form.name, form.email, form.password)
          : await register(form.name, form.email, form.password);
      } else {
        u = await login(form.email, form.password);
      }
      toast.success(isRegister ? "Konto skapat – välkommen!" : "Välkommen tillbaka!");
      const dest = (u?.role === "employer" || (isEmployer && u?.role === "admin")) ? "/foretag" : "/app";
      navigate(dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1613140957706-36e7299745b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Nordisk skog"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "hsl(var(--brand-navy) / 0.86)" }} />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <img src="/hamn-logo.png" alt="HAMN" className="h-12 w-12 rounded-xl object-cover ring-1 ring-white/20" />
            <div>
              <div className="font-head font-bold text-xl">HAMN CV Studio</div>
              <div className="text-xs uppercase tracking-widest opacity-80">Harmoni · AI · Människa · Natur</div>
            </div>
          </div>
          <div className="max-w-md">
            <h2 className="font-head text-4xl font-bold leading-tight">{isEmployer ? "Hitta rätt person – bortom CV:t." : "Din verkliga kompetens, samlad en gång."}</h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              {isEmployer
                ? "Sök kandidater på både roll och karaktär. Du ser bara HAMN-id först – matchning och potential i fokus, aldrig namn, ålder eller kön."
                : "Bygg din Masterprofil och låt AI skapa professionella, ATS-vänliga CV:n skräddarsydda mot varje jobb – utan att någonsin hitta på."}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <ShieldCheck className="h-4 w-4" /> Privacy-by-design. Din data är din.
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="hamn-bg flex items-center justify-center p-6">
        <Card className="w-full max-w-md rounded-2xl border-border p-8 md:p-10">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5" data-testid="auth-home-link">
            <ArrowLeft className="h-4 w-4" /> Till startsidan
          </Link>
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/hamn-logo.png" alt="HAMN" className="h-9 w-9 rounded-lg object-cover" />
            <span className="font-head font-bold text-primary text-lg">HAMN CV Studio</span>
          </div>
          <h1 className="font-head text-3xl font-bold">{isRegister ? (isEmployer ? "Skapa företagskonto" : "Skapa konto") : "Logga in"}</h1>
          <p className="text-muted-foreground mt-2 mb-6">
            {isRegister
              ? (isEmployer ? "Kom igång och hitta rätt person på minuter." : "Kom igång med din Masterprofil på några minuter.")
              : (isEmployer ? "Logga in på ditt arbetsgivarkonto." : "Fortsätt bygga din profil och dina CV:n.")}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && isEmployer && (
              <div className="space-y-2">
                <Label htmlFor="company">Företag</Label>
                <Input data-testid="company-input" id="company" required value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Företagets namn" />
              </div>
            )}
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name">{isEmployer ? "Kontaktperson" : "Namn"}</Label>
                <Input data-testid="name-input" id="name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ditt namn" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input data-testid="email-input" id="email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="namn@exempel.se" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Lösenord</Label>
                {!isRegister && (
                  <Link data-testid="forgot-password-link" to="/glomt-losenord" className="text-xs text-primary hover:underline">Glömt lösenord?</Link>
                )}
              </div>
              <Input data-testid="password-input" id="password" type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minst 6 tecken" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="auth-submit-btn" type="submit" className="w-full rounded-full h-11" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isRegister ? "Skapa konto" : "Logga in"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {isRegister ? (
              <>Har du redan ett konto? <Link data-testid="to-login-link" to={isEmployer ? "/foretag/logga-in" : "/login"} className="text-primary font-medium hover:underline">Logga in</Link></>
            ) : (
              <>Ny här? <Link data-testid="to-register-link" to={isEmployer ? "/foretag/registrera" : "/register"} className="text-primary font-medium hover:underline">Skapa konto</Link></>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            {isEmployer
              ? (<>Söker du jobb? <Link to="/login" className="text-primary hover:underline">Logga in som kandidat</Link></>)
              : (<>Är du arbetsgivare? <Link to="/foretag/logga-in" className="text-primary hover:underline">Till arbetsgivarsidan</Link></>)}
          </p>
        </Card>
      </div>
    </div>
  );
}
